import { useEffect, useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PayPal Smart Buttons for checkout. Fetches PayPal client_id from backend
 * (so we never hardcode it in frontend .env) and gracefully hides itself
 * when PayPal is not yet configured on the server.
 *
 * Props:
 *  - disabled: bool — disables the buttons while another payment is in flight
 *  - onSuccess(order_id): callback fired when capture completes
 */
export default function PayPalCheckoutButton({ disabled = false, onSuccess }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/payments/paypal/config`)
      .then((r) => { if (alive) setConfig(r.data); })
      .catch(() => { if (alive) setConfig({ enabled: false }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <div className="h-11 w-full rounded-md bg-muted animate-pulse" data-testid="paypal-loading" />;
  }

  if (!config?.enabled || !config?.client_id) {
    // PayPal not configured yet — hide the entire block. Site admin activates by adding keys.
    return null;
  }

  return (
    <div data-testid="paypal-buttons-container" className={disabled ? 'opacity-50 pointer-events-none' : ''}>
      <PayPalScriptProvider
        options={{
          'client-id': config.client_id,
          currency: config.currency || 'USD',
          intent: 'capture',
          components: 'buttons',
        }}
      >
        <PayPalButtons
          style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal', height: 44 }}
          disabled={disabled}
          createOrder={async () => {
            try {
              const r = await axios.post(`${API}/payments/paypal/create-order`);
              return r.data.paypal_order_id;
            } catch (err) {
              const msg = err.response?.data?.detail || 'Failed to create PayPal order';
              toast.error(msg);
              throw err;
            }
          }}
          onApprove={async (data) => {
            try {
              const r = await axios.post(`${API}/payments/paypal/capture-order`, {
                paypal_order_id: data.orderID,
              }, { timeout: 60000 }); // Give backend up to 60s (Printful can be slow)
              if (r.data.sandbox) {
                toast.info('Sandbox test capture completed. No real money was charged and no fulfillment was triggered.');
              } else {
                toast.success('Payment completed! Your order is being prepared.');
              }
              if (onSuccess) onSuccess(r.data.order_id);
            } catch (err) {
              // Frontend timeout does NOT necessarily mean the payment failed on the backend.
              // Show a message that avoids duplicate-purchase panic.
              const msg = err.response?.data?.detail
                || 'We couldn\'t confirm your order on-screen. Please check your email — if you received an order confirmation, your payment WAS successful. Do NOT retry the payment. If unsure, contact support with your PayPal transaction ID.';
              toast.error(msg, { duration: 10000 });
            }
          }}
          onCancel={() => {
            toast.info('PayPal checkout cancelled');
          }}
          onError={(err) => {
            console.error('PayPal SDK error:', err);
            toast.error('PayPal encountered an error. Please try Stripe instead.');
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
}
